import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TextInput,
  TouchableOpacity, ActivityIndicator, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import * as Clipboard from 'expo-clipboard';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useStorefront } from '../hooks/useStorefront';

// ─── Types ────────────────────────────────────────────────────────────────────

type OrderStatus = 'pending' | 'placed' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';

interface TrackingOrder {
  orderNumber: string;
  orderStatus: OrderStatus;
  paymentStatus: string;
  trackingNumber?: string;
  shippingMethodSnapshot?: { name: string; type: string; price: number; providerName?: string };
  items: Array<{ product: { name: string; primaryImage?: string }; quantity: number; unitPrice: number; totalPrice: number }>;
  subtotal?: number;
  tax?: number;
  extraCharges?: Array<{ name: string; amount: number }>;
  shipping?: number;
  discount?: number;
  totalPrice: number;
  currency: string;
  orderDate: string;
  confirmedAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
  cancelledReason?: string;
  invoiceUrl?: string;
}

interface HistoryItem {
  _id: string;
  orderNumber: string;
  orderStatus: OrderStatus;
  paymentStatus: string;
  totalPrice: number;
  currency: string;
  orderDate: string;
}

// ─── Status step config ───────────────────────────────────────────────────────

const STEPS: { status: OrderStatus; label: string; icon: string }[] = [
  { status: 'placed',     label: 'Order Placed',   icon: 'receipt-outline' },
  { status: 'confirmed',  label: 'Confirmed',       icon: 'checkmark-circle-outline' },
  { status: 'processing', label: 'Processing',      icon: 'construct-outline' },
  { status: 'shipped',    label: 'Shipped',         icon: 'car-outline' },
  { status: 'delivered',  label: 'Delivered',       icon: 'home-outline' },
];

const STATUS_ORDER: OrderStatus[] = ['pending', 'placed', 'confirmed', 'processing', 'shipped', 'delivered'];

type HistoryFilter = 'all' | 'active' | 'delivered' | 'cancelled';

const HISTORY_FILTERS: { key: HistoryFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

const HISTORY_PAGE_SIZE = 8;

const STATUS_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  delivered:  { bg: '#E8F5E9', text: '#2e7d32' },
  cancelled:  { bg: '#FDECEA', text: '#c0392b' },
  confirmed:  { bg: '#EDE7F6', text: '#5e35b1' },
  processing: { bg: '#E3F2FD', text: '#1565c0' },
  shipped:    { bg: '#E0F7FA', text: '#00695c' },
  placed:     { bg: '#FFF3E0', text: '#e65100' },
  pending:    { bg: '#F5F5F5', text: '#757575' },
};

function getBadgeStyle(status: string) {
  return STATUS_BADGE_COLORS[status] || { bg: '#EEF2FF', text: '#3949ab' };
}

function getStepIndex(status: OrderStatus): number {
  return STATUS_ORDER.indexOf(status);
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDateShort(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function stepTimestamp(order: TrackingOrder, status: OrderStatus): string | undefined {
  if (status === 'placed') return order.orderDate;
  if (status === 'confirmed') return order.confirmedAt;
  if (status === 'processing') return order.confirmedAt;
  if (status === 'shipped') return order.shippedAt;
  if (status === 'delivered') return order.deliveredAt;
}

function applyFilter(history: HistoryItem[], filter: HistoryFilter): HistoryItem[] {
  if (filter === 'all') return history;
  if (filter === 'active') return history.filter(h => !['delivered', 'cancelled'].includes(h.orderStatus));
  return history.filter(h => h.orderStatus === filter);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TrackOrderScreen: React.FC = () => {
  const s = useThemedStyles(getS);
  // Customers opening a tracking link get their device theme / link language.
  useStorefront();
  const { routeParams, showNotification } = useAppContext();
  const { currentBusiness } = useAppStore();
  const scrollRef = useRef<ScrollView>(null);

  const getParam = (key: string): string => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).get(key) || '';
    }
    return routeParams?.[key] || '';
  };

  const businessId = getParam('businessId') || currentBusiness?._id || '';
  const contactId = getParam('contactId');

  const [inputOrderNumber, setInputOrderNumber] = useState(() => getParam('orderNumber'));
  const [submittedOrderNumber, setSubmittedOrderNumber] = useState(() => getParam('orderNumber'));

  const [order, setOrder] = useState<TrackingOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('all');
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    if (businessId && submittedOrderNumber) fetchOrder(businessId, submittedOrderNumber);
    if (businessId && contactId) fetchHistory(businessId, contactId);
  }, [businessId]);

  const fetchOrder = async (bId: string, oNumber: string) => {
    if (!bId || !oNumber.trim()) { setError('Please enter your order number.'); return; }
    setLoading(true);
    setError(null);
    setOrder(null);
    try {
      const res = await apiService.trackOrder(bId, oNumber.trim());
      if (res.statusCode === 1000 && res.data) {
        const raw = res.data;
        setOrder({
          ...raw,
          subtotal: Number(raw.subtotal ?? 0),
          tax: Number(raw.tax ?? 0),
          shipping: Number(raw.shipping ?? 0),
          discount: Number(raw.discount ?? 0),
          totalPrice: Number(raw.totalPrice ?? 0),
          extraCharges: (raw.extraCharges ?? []).map((c: any) => ({ name: c.name, amount: Number(c.amount) })),
        });
        // Scroll to top so user sees the result immediately
        scrollRef.current?.scrollTo({ y: 0, animated: true });
      } else {
        setError(res.errors?.[0]?.message || 'Order not found. Please check your order number and try again.');
      }
    } catch {
      setError('Unable to fetch order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (bId: string, cId: string) => {
    setHistoryLoading(true);
    try {
      const res = await apiService.trackOrderHistory(bId, cId);
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setHistory(res.data.map((h: any) => ({ ...h, totalPrice: Number(h.totalPrice ?? 0) })));
      }
    } catch {
      // non-fatal
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSelectHistoryOrder = (orderNumber: string) => {
    setInputOrderNumber(orderNumber);
    setSubmittedOrderNumber(orderNumber);
    fetchOrder(businessId, orderNumber);
    // scroll to top happens inside fetchOrder on success
  };

  const handleTrack = () => {
    setSubmittedOrderNumber(inputOrderNumber);
    fetchOrder(businessId, inputOrderNumber);
  };

  const copyTracking = async (code: string) => {
    if (Platform.OS === 'web' && navigator.clipboard) {
      await navigator.clipboard.writeText(code);
    } else {
      await Clipboard.setStringAsync(code);
    }
    showNotification('success', 'Copied', 'Tracking number copied');
  };

  const isCancelled = order?.orderStatus === 'cancelled';
  const currentStepIndex = order ? getStepIndex(order.orderStatus) : -1;

  const filteredHistory = applyFilter(history, historyFilter);
  const pagedHistory = filteredHistory.slice(0, historyPage * HISTORY_PAGE_SIZE);
  const hasMore = pagedHistory.length < filteredHistory.length;

  const hasHistory = history.length > 1; // only show panel when there are multiple orders

  return (
    <ScrollView ref={scrollRef} style={s.root} contentContainerStyle={s.content} showsVerticalScrollIndicator={false}>

      {/* ── Header ── */}
      <View style={s.header}>
        <Ionicons name="cube-outline" size={28} color={colors.primary} />
        <Text style={s.headerTitle}>Track Your Order</Text>
        <Text style={s.headerSub}>Enter your order number to see the latest status.</Text>
      </View>

      {/* ── Search ── */}
      <View style={s.searchCard}>
        <TextInput
          style={s.searchInput}
          value={inputOrderNumber}
          onChangeText={setInputOrderNumber}
          placeholder="e.g. ORD-20240001"
          placeholderTextColor={colors.text.disabled as string}
          autoCapitalize="characters"
          returnKeyType="search"
          onSubmitEditing={handleTrack}
        />
        <TouchableOpacity style={s.trackBtn} onPress={handleTrack} disabled={loading} activeOpacity={0.8}>
          {loading
            ? <ActivityIndicator color={colors.text.onPrimary as string} size="small" />
            : <Text style={s.trackBtnText}>Track</Text>}
        </TouchableOpacity>
      </View>

      {/* ── Order History pills (shown when contact has multiple orders) ── */}
      {hasHistory && (
        <View style={s.historyPanel}>
          {/* Filter tabs */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.filterRow}>
            {HISTORY_FILTERS.map(f => (
              <TouchableOpacity
                key={f.key}
                style={[s.filterChip, historyFilter === f.key && s.filterChipActive]}
                onPress={() => { setHistoryFilter(f.key); setHistoryPage(1); }}
                activeOpacity={0.7}>
                <Text style={[s.filterChipText, historyFilter === f.key && s.filterChipTextActive]}>{f.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {historyLoading ? (
            <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.sm }} />
          ) : filteredHistory.length === 0 ? (
            <Text style={s.historyEmpty}>No orders in this category.</Text>
          ) : (
            <>
              {/* Horizontal order chips */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.orderChipsRow}>
                {pagedHistory.map(h => {
                  const isSelected = h.orderNumber === submittedOrderNumber;
                  const badge = getBadgeStyle(h.orderStatus);
                  return (
                    <TouchableOpacity
                      key={h._id}
                      style={[s.orderChip, isSelected && s.orderChipSelected]}
                      onPress={() => handleSelectHistoryOrder(h.orderNumber)}
                      activeOpacity={0.75}>
                      <View style={[s.chipStatusDot, { backgroundColor: badge.text }]} />
                      <View>
                        <Text style={[s.chipOrderNum, isSelected && { color: colors.primary }]}>
                          #{h.orderNumber}
                        </Text>
                        <Text style={s.chipDate}>{formatDateShort(h.orderDate)}</Text>
                      </View>
                      <View style={[s.chipBadge, { backgroundColor: badge.bg }]}>
                        <Text style={[s.chipBadgeText, { color: badge.text }]}>
                          {h.orderStatus.charAt(0).toUpperCase() + h.orderStatus.slice(1)}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
                {hasMore && (
                  <TouchableOpacity
                    style={s.showMoreChip}
                    onPress={() => setHistoryPage(p => p + 1)}
                    activeOpacity={0.7}>
                    <DirectionalIcon name="chevron-forward" size={14} color={colors.primary} />
                    <Text style={s.showMoreText}>More</Text>
                  </TouchableOpacity>
                )}
              </ScrollView>
            </>
          )}
        </View>
      )}

      {/* ── Error ── */}
      {error && (
        <View style={s.errorBox}>
          <Ionicons name="alert-circle-outline" size={18} color={colors.error as string} />
          <Text style={s.errorText}>{error}</Text>
        </View>
      )}

      {/* ── Result ── */}
      {order && (
        <View style={s.resultWrap}>

          {/* Order number + payment badge */}
          <View style={s.orderMeta}>
            <View style={{ flex: 1 }}>
              <Text style={s.orderNumber}>#{order.orderNumber}</Text>
              <Text style={s.orderDate}>{formatDate(order.orderDate)}</Text>
            </View>
            <View style={[s.payBadge,
              order.paymentStatus === 'paid' ? s.payBadgePaid :
              order.paymentStatus === 'partial' ? s.payBadgePartial : s.payBadgeUnpaid]}>
              <Text style={[s.payBadgeText,
                order.paymentStatus === 'paid' ? s.payBadgeTextPaid :
                order.paymentStatus === 'partial' ? s.payBadgeTextPartial : s.payBadgeTextUnpaid]}>
                {order.paymentStatus?.toUpperCase() || 'UNPAID'}
              </Text>
            </View>
          </View>

          {/* ── Status stepper / cancelled ── */}
          {isCancelled ? (
            <View style={s.cancelledBox}>
              <Ionicons name="close-circle" size={32} color={colors.error as string} />
              <Text style={s.cancelledTitle}>Order Cancelled</Text>
              {order.cancelledReason ? <Text style={s.cancelledReason}>{order.cancelledReason}</Text> : null}
              {order.cancelledAt ? <Text style={s.cancelledDate}>{formatDate(order.cancelledAt)}</Text> : null}
            </View>
          ) : (
            <View style={s.stepperCard}>
              {STEPS.map((step, idx) => {
                const done = currentStepIndex >= getStepIndex(step.status);
                const active = currentStepIndex === getStepIndex(step.status);
                const ts = stepTimestamp(order, step.status);
                const isLast = idx === STEPS.length - 1;
                return (
                  <View key={step.status} style={s.stepRow}>
                    <View style={s.stepLeft}>
                      <View style={[s.stepDot, done && s.stepDotDone, active && s.stepDotActive]}>
                        <Ionicons name={done ? 'checkmark' : step.icon as any} size={14} color={(done ? colors.text.onPrimary : colors.text.disabled) as string} />
                      </View>
                      {!isLast && <View style={[s.stepLine, done && s.stepLineDone]} />}
                    </View>
                    <View style={[s.stepBody, isLast && { marginBottom: 0 }]}>
                      <Text style={[s.stepLabel, active && s.stepLabelActive, done && !active && s.stepLabelDone]}>
                        {step.label}
                      </Text>
                      {ts && done ? <Text style={s.stepDate}>{formatDate(ts)}</Text> : null}
                    </View>
                  </View>
                );
              })}
            </View>
          )}

          {/* ── Tracking number ── */}
          {order.trackingNumber ? (
            <View style={s.trackingBox}>
              <Ionicons name="location-outline" size={18} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={s.trackingLabel}>Tracking Number</Text>
                <Text style={s.trackingValue}>{order.trackingNumber}</Text>
              </View>
              <TouchableOpacity onPress={() => copyTracking(order.trackingNumber!)} activeOpacity={0.7}>
                <Ionicons name="copy-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
            </View>
          ) : null}

          {/* ── Shipping method ── */}
          {order.shippingMethodSnapshot?.name ? (
            <View style={s.shippingBox}>
              <Ionicons name="car-outline" size={16} color={colors.text.secondary as string} />
              <Text style={s.shippingText}>{order.shippingMethodSnapshot.name}</Text>
            </View>
          ) : null}

          {/* ── Items ── */}
          <View style={s.section}>
            <Text style={s.sectionTitle}>Items</Text>
            {order.items.map((item, i) => (
              <View key={i} style={s.itemRow}>
                <Text style={s.itemName} numberOfLines={1}>{item.product?.name || '—'}</Text>
                <Text style={s.itemQty}>×{item.quantity}</Text>
                <Text style={s.itemPrice}>{order.currency} {(item.totalPrice || 0).toFixed(2)}</Text>
              </View>
            ))}
          </View>

          {/* ── Price summary ── */}
          <View style={s.section}>
            <Text style={s.sectionTitle}>Summary</Text>
            <View style={s.summaryCard}>
              {order.subtotal != null && (
                <View style={s.summaryRow}>
                  <Text style={s.summaryLabel}>Subtotal</Text>
                  <Text style={s.summaryValue}>{order.currency} {order.subtotal.toFixed(2)}</Text>
                </View>
              )}
              {order.tax != null && order.tax > 0 && (
                <View style={s.summaryRow}>
                  <Text style={s.summaryLabel}>Tax</Text>
                  <Text style={s.summaryValue}>{order.currency} {order.tax.toFixed(2)}</Text>
                </View>
              )}
              {(order.extraCharges ?? []).filter(c => c.amount > 0).map((c, i) => (
                <View key={i} style={s.summaryRow}>
                  <Text style={s.summaryLabel}>{c.name}</Text>
                  <Text style={s.summaryValue}>{order.currency} {Number(c.amount).toFixed(2)}</Text>
                </View>
              ))}
              {order.shipping != null && order.shipping > 0 && (
                <View style={s.summaryRow}>
                  <Text style={s.summaryLabel}>Shipping</Text>
                  <Text style={s.summaryValue}>{order.currency} {order.shipping.toFixed(2)}</Text>
                </View>
              )}
              {order.discount != null && order.discount > 0 && (
                <View style={s.summaryRow}>
                  <Text style={s.summaryLabel}>Discount</Text>
                  <Text style={[s.summaryValue, { color: colors.success }]}>−{order.currency} {order.discount.toFixed(2)}</Text>
                </View>
              )}
              <View style={[s.summaryRow, s.summaryTotal]}>
                <Text style={s.summaryTotalLabel}>Total</Text>
                <Text style={s.summaryTotalValue}>{order.currency} {Number(order.totalPrice).toFixed(2)}</Text>
              </View>
            </View>
          </View>

          {/* ── Invoice ── */}
          {order.invoiceUrl ? (
            <TouchableOpacity
              style={s.invoiceBtn}
              onPress={() => { if (Platform.OS === 'web') window.open(order.invoiceUrl!, '_blank'); }}
              activeOpacity={0.8}>
              <Ionicons name="document-text-outline" size={18} color={colors.primary} />
              <Text style={s.invoiceBtnText}>Download Invoice</Text>
            </TouchableOpacity>
          ) : null}

        </View>
      )}

    </ScrollView>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getS = () => StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.background.secondary },
  content: { padding: spacing.md, paddingBottom: 48, gap: spacing.md, maxWidth: 540, alignSelf: 'center', width: '100%' },

  header: { alignItems: 'center', paddingVertical: spacing.lg, gap: spacing.xs },
  headerTitle: { fontSize: 22, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  headerSub: { fontSize: typography.fontSize.sm, color: colors.text.secondary, textAlign: 'center' },

  searchCard: { flexDirection: 'row', gap: spacing.sm, backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, padding: spacing.sm },
  searchInput: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular, paddingVertical: 4 },
  trackBtn: { backgroundColor: colors.primary, borderRadius: 8, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, justifyContent: 'center' },
  trackBtnText: { color: colors.text.onPrimary, fontFamily: typography.fontFamily.semibold, fontSize: typography.fontSize.sm },

  // ─── History panel ───────────────────────────────────────────────────────
  historyPanel: { backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, overflow: 'hidden' },

  filterRow: { flexDirection: 'row', gap: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  filterChip: { paddingHorizontal: spacing.sm, paddingVertical: 5, borderRadius: 20, backgroundColor: colors.background.secondary },
  filterChipActive: { backgroundColor: colors.primary },
  filterChipText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  filterChipTextActive: { color: colors.text.onPrimary },

  historyEmpty: { fontSize: typography.fontSize.sm, color: colors.text.secondary, textAlign: 'center', paddingVertical: spacing.md },

  orderChipsRow: { flexDirection: 'row', gap: spacing.sm, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm },
  orderChip: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.background.secondary, borderRadius: 10, borderWidth: 1, borderColor: colors.border, paddingHorizontal: spacing.sm, paddingVertical: 8, minWidth: 130 },
  orderChipSelected: { borderColor: colors.primary, backgroundColor: colors.primaryTransparent },
  chipStatusDot: { width: 7, height: 7, borderRadius: 4 },
  chipOrderNum: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  chipDate: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 1 },
  chipBadge: { borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2, marginLeft: 'auto' as any },
  chipBadgeText: { fontSize: 9, fontFamily: typography.fontFamily.semibold },

  showMoreChip: { flexDirection: 'row', alignItems: 'center', gap: 4, justifyContent: 'center', backgroundColor: colors.primaryTransparent, borderRadius: 10, borderWidth: 1, borderColor: colors.primary, paddingHorizontal: spacing.sm, paddingVertical: 8, minWidth: 70 },
  showMoreText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },

  // ─── Error / result ───────────────────────────────────────────────────────
  errorBox: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, backgroundColor: colors.background.secondary, borderRadius: 8, padding: spacing.sm, borderWidth: 1, borderColor: colors.error },
  errorText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.error, fontFamily: typography.fontFamily.regular },

  resultWrap: { gap: spacing.md },

  orderMeta: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.background.surface, borderRadius: 12, padding: spacing.md, borderWidth: 1, borderColor: colors.border },
  orderNumber: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  orderDate: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 2 },
  payBadge: { borderRadius: 6, paddingHorizontal: spacing.sm, paddingVertical: 3 },
  payBadgePaid: { backgroundColor: '#E8F5E9' },
  payBadgePartial: { backgroundColor: '#FFF8E1' },
  payBadgeUnpaid: { backgroundColor: '#FDECEA' },
  payBadgeText: { fontSize: 11, fontFamily: typography.fontFamily.semibold },
  payBadgeTextPaid: { color: '#2e7d32' },
  payBadgeTextPartial: { color: '#F57F17' },
  payBadgeTextUnpaid: { color: colors.error },

  cancelledBox: { backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.error, padding: spacing.lg, alignItems: 'center', gap: spacing.xs },
  cancelledTitle: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.semibold, color: colors.error },
  cancelledReason: { fontSize: typography.fontSize.sm, color: colors.text.secondary, textAlign: 'center' },
  cancelledDate: { fontSize: typography.fontSize.xs, color: colors.text.secondary },

  stepperCard: { backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, padding: spacing.md },
  stepRow: { flexDirection: 'row', gap: spacing.sm },
  stepLeft: { alignItems: 'center', width: 28 },
  stepDot: { width: 28, height: 28, borderRadius: 14, backgroundColor: colors.background.secondary, borderWidth: 2, borderColor: colors.border, alignItems: 'center', justifyContent: 'center' },
  stepDotDone: { backgroundColor: colors.primary, borderColor: colors.primary },
  stepDotActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  stepLine: { flex: 1, width: 2, backgroundColor: colors.border, marginVertical: 2 },
  stepLineDone: { backgroundColor: colors.primary },
  stepBody: { flex: 1, paddingBottom: spacing.md },
  stepLabel: { fontSize: typography.fontSize.sm, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  stepLabelActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  stepLabelDone: { color: colors.text.primary, fontFamily: typography.fontFamily.medium },
  stepDate: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 2 },

  trackingBox: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, padding: spacing.md },
  trackingLabel: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  trackingValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, letterSpacing: 0.5 },

  shippingBox: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingHorizontal: spacing.xs },
  shippingText: { fontSize: typography.fontSize.sm, color: colors.text.secondary },

  section: { gap: spacing.xs },
  sectionTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary, textTransform: 'uppercase', letterSpacing: 0.6 },
  itemRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.background.surface, borderRadius: 10, borderWidth: 1, borderColor: colors.border, padding: spacing.sm, gap: spacing.sm },
  itemName: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.medium },
  itemQty: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  itemPrice: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },

  summaryCard: { backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, overflow: 'hidden' },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  summaryLabel: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  summaryValue: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.medium },
  summaryTotal: { borderBottomWidth: 0, backgroundColor: colors.background.secondary },
  summaryTotalLabel: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  summaryTotalValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.primary },

  invoiceBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, borderWidth: 1, borderColor: colors.primary, borderRadius: 10, paddingVertical: spacing.sm },
  invoiceBtnText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
});
